import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Trash2, Loader2, Mail, Copy, ExternalLink, BarChart3, Users, ShoppingCart, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function OnboardingDraftsPage() {
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/agency/onboarding/drafts"),
        api.get("/agency/onboarding/funnel-stats"),
      ]);
      setDrafts(d.data); setStats(s.data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    await api.delete(`/agency/onboarding/drafts/${id}`);
    toast.success("Supprimé"); load();
  };

  const copyResumeUrl = (token) => {
    const url = `${window.location.origin}/onboarding?draft=${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien de reprise copié — tu peux l'envoyer au prospect");
  };

  const sendResumeEmail = (draft) => {
    if (!draft.contact_email) return toast.error("Pas d'email saisi par le prospect");
    const url = `${window.location.origin}/onboarding?draft=${draft.draft_token}`;
    const subject = encodeURIComponent("Finalisez votre demande de site");
    const body = encodeURIComponent(`Bonjour ${draft.contact_first_name || ""},\n\nVous avez commencé une demande de site sur notre plateforme. Reprenez où vous en étiez :\n${url}\n\nÀ très vite,\nL'équipe`);
    window.open(`mailto:${draft.contact_email}?subject=${subject}&body=${body}`, "_blank");
  };

  const completionColor = (p) => {
    if (p >= 75) return "bg-emerald-500";
    if (p >= 50) return "bg-amber-500";
    if (p >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl" data-testid="onboarding-drafts-page">
      <div>
        <h1 className="text-2xl font-bold">Tunnel d'onboarding</h1>
        <p className="text-sm text-slate-500">Brouillons abandonnés en cours de remplissage + statistiques de conversion.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase text-slate-500">
              <Users className="w-4 h-4" /> Brouillons
            </div>
            <div className="text-2xl font-bold mt-1">{stats.total_drafts}</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase text-slate-500">
              <ShoppingCart className="w-4 h-4" /> Demandes finalisées
            </div>
            <div className="text-2xl font-bold mt-1">{stats.total_requests}</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase text-slate-500">
              <BarChart3 className="w-4 h-4" /> Taux conversion
            </div>
            <div className="text-2xl font-bold mt-1 text-emerald-600">{stats.tunnel_completion_rate}%</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase text-slate-500 mb-1">Drafts par avancement</div>
            <div className="space-y-1 text-xs">
              {Object.entries(stats.drafts_by_completion).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">{k}</span>
                  <span className="font-bold">{v}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {drafts.length === 0 && !loading && (
        <Card className="p-10 text-center text-sm text-slate-500">
          <AlertCircle className="w-8 h-8 mx-auto opacity-40 mb-2" />
          Aucun brouillon — c'est parfait ! Tous les prospects qui démarrent un onboarding vont jusqu'au bout.
        </Card>
      )}

      <div className="space-y-2">
        {drafts.map(d => (
          <Card key={d.id} className="p-4" data-testid={`draft-row-${d.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <strong>{d.company_name || "Société non renseignée"}</strong>
                  {d.plan_code && <Badge variant="outline">{d.plan_code}</Badge>}
                  <Badge variant="secondary" className="text-[10px]">step {d.last_step_completed}/4</Badge>
                </div>
                <div className="text-xs text-slate-500">
                  {d.contact_first_name && `${d.contact_first_name} `}
                  {d.contact_last_name || ""}
                  {d.contact_email ? ` · ${d.contact_email}` : ""}
                  {d.contact_phone ? ` · ${d.contact_phone}` : ""}
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Token <code className="font-mono">{d.draft_token?.slice(0, 12)}…</code> · MAJ {(d.updated_at || "").slice(0, 16).replace("T", " ")}
                </div>
                {/* Progress bar */}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${completionColor(d.completion_percent || 0)}`} style={{ width: `${d.completion_percent || 0}%` }} />
                  </div>
                  <span className="text-xs font-bold w-10 text-right">{d.completion_percent || 0}%</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button size="sm" variant="outline" onClick={() => copyResumeUrl(d.draft_token)} title="Copier le lien de reprise">
                  <Copy className="w-3 h-3" />
                </Button>
                {d.contact_email && (
                  <Button size="sm" variant="outline" onClick={() => sendResumeEmail(d)} title="Relancer par email">
                    <Mail className="w-3 h-3" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="text-red-500" title="Supprimer">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
